import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Json } from '@/integrations/supabase/types';
import { usePushNotifications } from './usePushNotifications';

type NotificationType = 'signal' | 'trade' | 'price_alert' | 'risk_warning' | 'system';

interface NotificationPayload {
  type: NotificationType;
  title: string;
  message: string;
  action_url?: string;
  metadata?: Record<string, unknown>;
}

export function useSmartNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { 
    isSubscribed: pushEnabled,
    notifyPriceAlert: pushPriceAlert,
    notifyNewSignal: pushNewSignal,
    notifyTradeUpdate: pushTradeUpdate,
    notifyRiskWarning: pushRiskWarning,
  } = usePushNotifications();

  // Create a notification in the database
  const createNotification = useCallback(async (payload: NotificationPayload) => {
    if (!user?.id) return;

    try {
      await supabase.from('notifications').insert([{
        user_id: user.id,
        type: payload.type,
        title: payload.title,
        message: payload.message,
        action_url: payload.action_url,
        metadata: payload.metadata as Json | null,
      }]);
      
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      console.error('Failed to create notification:', error);
    }
  }, [user?.id, queryClient]);

  // Show toast notification
  const showToast = useCallback((payload: NotificationPayload) => {
    const iconMap: Record<NotificationType, string> = {
      signal: '📊',
      trade: '💰',
      price_alert: '🔔',
      risk_warning: '⚠️',
      system: 'ℹ️',
    };

    const toastOptions: Parameters<typeof toast>[1] = {
      description: payload.message,
      action: payload.action_url ? {
        label: 'View',
        onClick: () => window.location.href = payload.action_url!,
      } : undefined,
    };

    switch (payload.type) {
      case 'risk_warning':
        toast.warning(`${iconMap[payload.type]} ${payload.title}`, toastOptions);
        break;
      case 'trade':
        if (payload.metadata?.pnl && (payload.metadata.pnl as number) > 0) {
          toast.success(`${iconMap[payload.type]} ${payload.title}`, toastOptions);
        } else {
          toast.info(`${iconMap[payload.type]} ${payload.title}`, toastOptions);
        }
        break;
      case 'signal':
        toast.info(`${iconMap[payload.type]} ${payload.title}`, toastOptions);
        break;
      default:
        toast(` ${iconMap[payload.type]} ${payload.title}`, toastOptions);
    }
  }, []);

  // Notify about new signal
  const notifyNewSignal = useCallback(async (signal: {
    symbol: string;
    direction: string;
    strategy: string;
    entry_price?: number;
    confluence_score?: number;
  }) => {
    const payload: NotificationPayload = {
      type: 'signal',
      title: `New ${signal.direction} Signal: ${signal.symbol}`,
      message: `${signal.strategy} strategy detected a ${signal.direction.toLowerCase()} opportunity with ${signal.confluence_score || 3}/5 confluence.`,
      action_url: '/markets',
      metadata: signal,
    };
    
    showToast(payload);
    await createNotification(payload);
    
    // Also trigger push notification if enabled
    if (pushEnabled && signal.entry_price) {
      pushNewSignal({
        symbol: signal.symbol,
        direction: signal.direction,
        strategy: signal.strategy,
        entry_price: signal.entry_price,
        confluence_score: signal.confluence_score,
      });
    }
  }, [showToast, createNotification, pushEnabled, pushNewSignal]);

  // Notify about trade opened
  const notifyTradeOpened = useCallback(async (trade: {
    symbol: string;
    direction: string;
    quantity: number;
    entry_price: number;
    is_paper_trade?: boolean;
  }) => {
    const payload: NotificationPayload = {
      type: 'trade',
      title: `${trade.is_paper_trade ? '📝 Paper ' : ''}Trade Opened: ${trade.symbol}`,
      message: `${trade.direction} ${trade.quantity} shares at ₹${trade.entry_price.toFixed(2)}`,
      action_url: '/trades',
      metadata: trade,
    };
    
    showToast(payload);
    await createNotification(payload);
    
    // Also trigger push notification if enabled
    if (pushEnabled) {
      pushTradeUpdate({
        symbol: trade.symbol,
        status: 'opened',
        entry_price: trade.entry_price,
        quantity: trade.quantity,
      });
    }
  }, [showToast, createNotification, pushEnabled, pushTradeUpdate]);

  // Notify about trade closed
  const notifyTradeClosed = useCallback(async (trade: {
    symbol: string;
    pnl: number;
    pnl_percent: number;
    r_multiple?: number;
  }) => {
    const isProfit = trade.pnl > 0;
    const payload: NotificationPayload = {
      type: 'trade',
      title: `Trade Closed: ${trade.symbol}`,
      message: `${isProfit ? '🎉 Profit' : '📉 Loss'}: ₹${Math.abs(trade.pnl).toFixed(2)} (${trade.pnl_percent >= 0 ? '+' : ''}${trade.pnl_percent.toFixed(2)}%)${trade.r_multiple ? ` | ${trade.r_multiple.toFixed(1)}R` : ''}`,
      action_url: '/trades',
      metadata: { ...trade, isProfit },
    };
    
    showToast(payload);
    await createNotification(payload);
    
    // Also trigger push notification if enabled
    if (pushEnabled) {
      pushTradeUpdate({
        symbol: trade.symbol,
        status: 'closed',
        pnl: trade.pnl,
        pnl_percent: trade.pnl_percent,
      });
    }
  }, [showToast, createNotification, pushEnabled, pushTradeUpdate]);

  // Notify about price alert
  const notifyPriceAlert = useCallback(async (alert: {
    symbol: string;
    target_price: number;
    current_price: number;
    condition: string;
  }) => {
    const payload: NotificationPayload = {
      type: 'price_alert',
      title: `Price Alert: ${alert.symbol}`,
      message: `${alert.symbol} has ${alert.condition === 'above' ? 'crossed above' : 'dropped below'} ₹${alert.target_price.toFixed(2)} (now ₹${alert.current_price.toFixed(2)})`,
      action_url: '/watchlist',
      metadata: alert,
    };
    
    showToast(payload);
    await createNotification(payload);
    
    // Also trigger push notification if enabled
    if (pushEnabled) {
      pushPriceAlert(alert);
    }
  }, [showToast, createNotification, pushEnabled, pushPriceAlert]);

  // Notify about risk warning
  const notifyRiskWarning = useCallback(async (warning: {
    type: 'portfolio_heat' | 'position_size' | 'drawdown' | 'vix';
    message: string;
    severity: 'low' | 'medium' | 'high';
  }) => {
    const payload: NotificationPayload = {
      type: 'risk_warning',
      title: `Risk Alert: ${warning.type.replace('_', ' ').toUpperCase()}`,
      message: warning.message,
      action_url: '/settings',
      metadata: warning,
    };
    
    showToast(payload);
    await createNotification(payload);
    
    // Also trigger push notification if enabled
    if (pushEnabled) {
      pushRiskWarning(warning);
    }
  }, [showToast, createNotification, pushEnabled, pushRiskWarning]);

  // Set up realtime subscriptions
  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to new signals
    const signalsChannel = supabase
      .channel('signals-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'signals',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const signal = payload.new as {
            symbol: string;
            direction: string;
            strategy: string;
            confluence_score?: number;
          };
          notifyNewSignal(signal);
        }
      )
      .subscribe();

    // Subscribe to trade updates
    const tradesChannel = supabase
      .channel('trades-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'trades',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const trade = payload.new as {
            status: string;
            symbol: string;
            pnl: number;
            pnl_percent: number;
            r_multiple?: number;
          };
          
          if (payload.old.status === 'open' && trade.status === 'closed') {
            notifyTradeClosed(trade);
          }
        }
      )
      .subscribe();

    // Subscribe to price alerts
    const alertsChannel = supabase
      .channel('price-alerts-notifications')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'price_alerts',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const alert = payload.new as {
            is_triggered: boolean;
            symbol: string;
            target_price: number;
            condition: string;
          };
          
          if (!payload.old.is_triggered && alert.is_triggered) {
            notifyPriceAlert({
              ...alert,
              current_price: alert.target_price, // Approximate
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(signalsChannel);
      supabase.removeChannel(tradesChannel);
      supabase.removeChannel(alertsChannel);
    };
  }, [user?.id, notifyNewSignal, notifyTradeClosed, notifyPriceAlert]);

  return {
    notifyNewSignal,
    notifyTradeOpened,
    notifyTradeClosed,
    notifyPriceAlert,
    notifyRiskWarning,
    createNotification,
    showToast,
  };
}
